import { motion } from '@carbon/motion';
import styled, { css } from 'styled-components';
import { getTextStyle } from '../../content/texts/bou-text/text-style';
import { BouColor } from '../../global/bou-colors';
import { BouSpacingLevel, fluidSpacing } from '../../layout/bou-spacing';
import { MotionDuration, MotionMode, MotionName } from '../../motion/types';
import { ButtonBaseProps, ButtonColorProps, ButtonSize } from './types';

type NiceButtonProps = ButtonColorProps & ButtonBaseProps;

/* RN: Use <NiceButton> to get the default styling and add props if needed.
   Todo: rename this at some point more descriptively and perhaps define reusable components for different use cases (e.g. default/primary/danger) 
*/
export const NiceButton = styled.button<NiceButtonProps>`
  ${props => getNiceButtonStyle(props)}
`;

export function getNiceButtonStyle(props: NiceButtonProps) {
  return css`
    ${getButtonBaseStyle(props)}
    ${getButtonColorStyle(props)}
  `.join('');
}

/* RN: getButtonBaseStyle() offers default sizing, plus smaller and larger variations. The default works for most contexts.
   Use smaller for places where the default feels awkwardly large, and the large mostly for marketing pages/really prominent calls to action.
*/
export function getButtonBaseStyle(props?: ButtonBaseProps) {
  return css`
    ${getSizeDependentProperties(props?.size)}
    line-height: 140%;
    border-radius: 3px;
    align-self: center; // RN: Prevents button from stretching vertically inside flexbox.
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    cursor: pointer;
    transition: ${MotionDuration.FAST1_70ms}ms ${motion(MotionName.STANDARD, MotionMode.PRODUCTIVE)};
    transition-property: color, background-color, filter;
    & + & {
      ${fluidSpacing('margin-left', BouSpacingLevel.TINY)}
    }
  `.join('');
}

function getSizeDependentProperties(size?: ButtonSize) {
  switch (size) {
    case 'larger':
      return `
          ${getTextStyle({ size: 'xsmall' })}
          font-weight: 600;
          padding: 0.75em 1em;
      `;
    case 'smaller':
      return `
          ${getTextStyle({ size: 'xxxxs' })}
          font-weight: 500;
          padding: 0.2em 0.4em;
      `;
    default:
      return `
          ${getTextStyle({ size: 'xxxs' })}
          font-weight: 500;
          padding: 0.5em 1em;
      `;
  }
}

export function getButtonColorStyle(props: ButtonColorProps) {
  switch (props.theme) {
    case 'dark':
      return darkTheme(props.isOutlined);
    case 'light':
      return lightTheme(props.isOutlined);
    case 'purple':
      return purpleTheme(props.isOutlined);
    case 'turquoise':
      return turquoiseTheme(props.isOutlined);
    case 'yellow':
      return yellowTheme(props.isOutlined);
    default:
      return darkTheme(props.isOutlined);
  }
}

function darkTheme(isOutlined?: boolean) {
  return css`
    color: ${isOutlined ? BouColor.TOOL_BACKGROUND_MUTED_BLACK : BouColor.TOOL_TEXT_WHITE};
    border: 1px solid ${BouColor.TOOL_BACKGROUND_MUTED_BLACK};
    background-color: ${isOutlined ? 'none' : BouColor.TOOL_BUTTON_BACKGROUND_DARK};
    &:hover {
      background-color: ${isOutlined ? BouColor.PLATFORM_BG_GRAY_LIGHTEST : BouColor.TOOL_BUTTON_BACKGROUND_DARK_HOVER};
    }
    ${commonDisabledStyles()}
  `.join('');
}

function lightTheme(isOutlined?: boolean) {
  return css`
    color: ${BouColor.TOOL_TEXT_BLACK};
    border: 1px solid ${BouColor.TOOL_BORDER_CARDS_LIGHT_GRAY};
    background-color: ${isOutlined ? 'none' : BouColor.TOOL_BUTTON_BACKGROUND_LIGHT};
    &:hover {
      color: ${isOutlined ? BouColor.TOOL_TEXT_BLACK : ''};
      background-color: ${isOutlined ? BouColor.TOOL_TEXT_WHITE : BouColor.TOOL_BUTTON_BACKGROUND_LIGHT_HOVER};
    }
    ${commonDisabledStyles()}
  `.join('');
}

function purpleTheme(isOutlined?: boolean) {
  return css`
    color: ${isOutlined ? BouColor.IMPACT_NEGATIVE_PURPLE : BouColor.TOOL_TEXT_WHITE};
    border: 1px solid ${BouColor.IMPACT_NEGATIVE_PURPLE};
    background-color: ${isOutlined ? 'none' : BouColor.IMPACT_NEGATIVE_PURPLE};
    &:hover {
      color: ${isOutlined ? BouColor.TOOL_TEXT_WHITE : ''};
      background-color: ${BouColor.IMPACT_NEGATIVE_PURPLE};
      filter: ${isOutlined ? '' : 'brightness(120%)'};
    }
    ${commonDisabledStyles()}
  `.join('');
}

function turquoiseTheme(isOutlined?: boolean) {
  return css`
    color: ${isOutlined ? BouColor.IMPACT_POSITIVE_GREEN : BouColor.TOOL_TEXT_BLACK};
    border: 1px solid ${BouColor.IMPACT_POSITIVE_GREEN};
    background-color: ${isOutlined ? 'none' : BouColor.IMPACT_POSITIVE_GREEN};
    &:hover {
      color: ${isOutlined ? BouColor.TOOL_TEXT_WHITE : ''};
      background-color: ${BouColor.IMPACT_POSITIVE_GREEN};
      filter: ${isOutlined ? '' : 'brightness(105%)'};
    }
    ${commonDisabledStyles()}
  `.join('');
}

function yellowTheme(isOutlined?: boolean) {
  return css`
    color: ${isOutlined ? BouColor.TOOL_BUTTON_BACKGROUND_YELLOW : BouColor.TOOL_TEXT_BLACK};
    border: 1px solid ${BouColor.TOOL_BUTTON_BACKGROUND_YELLOW};
    background-color: ${isOutlined ? 'none' : BouColor.TOOL_BUTTON_BACKGROUND_YELLOW};
    &:hover {
      color: ${isOutlined ? BouColor.TOOL_TEXT_WHITE : ''};
      background-color: ${BouColor.TOOL_BUTTON_BACKGROUND_YELLOW};
      filter: ${isOutlined ? '' : 'brightness(105%)'};
    }
    ${commonDisabledStyles()}
  `.join('');
}

function commonDisabledStyles() {
  return css`
    &:disabled,
    &:disabled:hover {
      color: ${BouColor.TOOL_BUTTON_TEXT_DISABLED_GRAY};
      background-color: ${BouColor.TOOL_BUTTON_BACKGROUND_DISABLED_GRAY};
      border-color: ${BouColor.TOOL_BUTTON_BACKGROUND_DISABLED_GRAY};
      cursor: default;
    }
  `.join('');
}
